import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_LANGUAGE,
  detectBrowserLanguage,
  getLanguageFromPath,
  getStoredLanguage,
  storeLanguage,
  buildLocalizedPath,
  stripLanguageFromPath,
  type LanguageCode,
} from './config';
import { en, type Translations } from './translations/en';
import { es } from './translations/es';
import { fr } from './translations/fr';
import { it } from './translations/it';
import { ru } from './translations/ru';
import { de } from './translations/de';
import { pt } from './translations/pt';

const translationMap: Record<LanguageCode, Translations> = {
  en,
  es: es as Translations,
  fr: fr as Translations,
  it: it as Translations,
  ru: ru as Translations,
  de: de as Translations,
  pt: pt as Translations,
};

interface I18nContextValue {
  lang: LanguageCode;
  t: Translations;
  changeLanguage: (lang: LanguageCode) => void;
  localizedPath: (path: string) => string;
  stripLang: (path: string) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

interface I18nProviderProps {
  children: React.ReactNode;
  initialLang?: LanguageCode;
}

export function I18nProvider({ children, initialLang }: I18nProviderProps) {
  const [lang, setLang] = useState<LanguageCode>(initialLang ?? DEFAULT_LANGUAGE);

  useEffect(() => {
    const pathLang = getLanguageFromPath(window.location.pathname);
    const stored = getStoredLanguage();
    const browser = detectBrowserLanguage();

    const resolved = pathLang !== DEFAULT_LANGUAGE ? pathLang : (stored ?? browser ?? DEFAULT_LANGUAGE);

    setLang(resolved);

    if (resolved !== DEFAULT_LANGUAGE && pathLang === DEFAULT_LANGUAGE && resolved !== getLanguageFromPath(window.location.pathname)) {
      const newPath = buildLocalizedPath(window.location.pathname + window.location.hash, resolved);
      window.history.replaceState(null, '', newPath);
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const changeLanguage = useCallback((nextLang: LanguageCode) => {
    setLang(nextLang);
    storeLanguage(nextLang);

    const currentPath = window.location.pathname + window.location.hash + window.location.search;
    const stripped = stripLanguageFromPath(currentPath);
    const newPath = buildLocalizedPath(stripped, nextLang);
    window.history.pushState(null, '', newPath);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);

  const localizedPath = useCallback(
    (path: string) => buildLocalizedPath(path, lang),
    [lang],
  );

  const stripLang = useCallback((path: string) => stripLanguageFromPath(path), []);

  const value = useMemo<I18nContextValue>(
    () => ({
      lang,
      t: translationMap[lang] ?? en,
      changeLanguage,
      localizedPath,
      stripLang,
    }),
    [lang, changeLanguage, localizedPath, stripLang],
  );

  return React.createElement(I18nContext.Provider, { value }, children);
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return ctx;
}
